import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import LocationMap from './components/LocationMap';
import SuccessModal from './components/SuccessModal';

const ContactPage = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);

  const handleFormSubmit = (data) => {
    setSubmissionData(data);
    setShowSuccessModal(true);
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setSubmissionData(null);
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - Workfolio | Get in Touch</title>
        <meta 
          name="description" 
          content="Contact Workfolio for support, partnerships, or general inquiries. We're here to help with your anonymous recruitment needs." 
        />
        <meta name="keywords" content="contact, support, workfolio, recruitment, help" />
        <meta property="og:title" content="Contact Us - Workfolio" />
        <meta property="og:description" content="Get in touch with Workfolio for support and inquiries" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://workfolio.no/contact" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="container mx-auto px-6 lg:px-8 py-12">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-semibold text-foreground mb-4">
              Contact Us
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Have questions about Workfolio? We're here to help. Send us a message and we'll get back to you as soon as possible.
            </p>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {/* Contact Form - Takes 2 columns on large screens */}
            <div className="lg:col-span-2">
              <ContactForm onSubmit={handleFormSubmit} />
            </div>

            {/* Contact Information - Takes 1 column on large screens */}
            <div className="lg:col-span-1">
              <ContactInfo />
            </div>
          </div>

          {/* Location Map - Full width */}
          <div className="mb-12">
            <LocationMap />
          </div>

          {/* Additional Support Section */}
          <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-8 border border-primary/20">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                Need Immediate Help?
              </h2>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                For urgent technical issues or account problems, check our FAQ section 
                for quick solutions or contact our support team directly.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/homepage#faq"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-card text-foreground font-medium hover:shadow-elevation transition-all"
                >
                  Browse FAQ
                </a>
                <a
                  href="mailto:support@workfolio.no"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                >
                  Email Support
                </a>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-secondary text-secondary-foreground py-12 mt-16">
          <div className="container mx-auto px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="md:col-span-2">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                      <line x1="8" y1="21" x2="16" y2="21"/>
                      <line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                  </div>
                  <span className="text-xl font-semibold">Workfolio</span>
                </div>
                <p className="text-secondary-foreground/80 mb-4 max-w-md">
                  Anonymous, skills-first recruitment platform connecting talented 
                  job seekers with forward-thinking employers.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-4">Quick Links</h3>
                <div className="space-y-2">
                  <a href="/homepage" className="block text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">
                    Home
                  </a>
                  <a href="/homepage#how-it-works" className="block text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">
                    How It Works
                  </a>
                  <a href="/homepage#pricing" className="block text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">
                    Pricing
                  </a>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold mb-4">Support</h3>
                <div className="space-y-2">
                  <a href="/contact" className="block text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">
                    Contact Us
                  </a>
                  <a href="/homepage#faq" className="block text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">
                    FAQ
                  </a>
                  <a href="mailto:support@workfolio.no" className="block text-secondary-foreground/80 hover:text-secondary-foreground transition-colors">
                    Email Support
                  </a>
                </div>
              </div>
            </div>
            
            <div className="border-t border-secondary-foreground/20 mt-8 pt-8 text-center">
              <p className="text-secondary-foreground/60">
                © {new Date()?.getFullYear()} Workfolio. All rights reserved.
              </p>
            </div>
          </div>
        </footer>

        {/* Success Modal */}
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={handleCloseModal}
          submissionData={submissionData}
        />
      </div>
    </>
  );
};

export default ContactPage;